import { TrendingDown, TrendingUp } from "lucide-react";

import type { WeeklySummary } from "@/types/activity";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface WeeklyDigestProps {
  summary: WeeklySummary | undefined;
  isLoading: boolean;
  isError?: boolean;
  onAttentionClick?: () => void;
  onSilentClick?: () => void;
}

const formatDelta = (delta: number | null): { label: string; tone: "up" | "down" | "flat" | "new" } => {
  if (delta === null) return { label: "no prior data", tone: "new" };
  if (delta === 0) return { label: "no change", tone: "flat" };
  const sign = delta > 0 ? "+" : "";
  return { label: `${sign}${delta}% vs last week`, tone: delta > 0 ? "up" : "down" };
};

const Tile = ({
  genieIcon,
  label,
  value,
  footer,
  highlight,
  onClick,
}: {
  genieIcon: GenieIconName;
  label: string;
  value: string;
  footer: React.ReactNode;
  highlight?: boolean;
  onClick?: () => void;
}) => {
  const interactive = !!onClick;
  const Wrapper: "button" | "div" = interactive ? "button" : "div";

  const inner = (
    <div className="rounded-[14px] bg-white p-5 md:p-6 text-left h-full flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl bg-genie-primary/10 flex items-center justify-center">
          <GenieIcon name={genieIcon} size={24} className="h-6 w-6" />
        </div>
        <p className="text-sm font-medium text-[#64748B]">{label}</p>
      </div>
      <p className="text-3xl font-bold text-[#222B45] leading-none mb-2">{value}</p>
      <div className="mt-auto pt-3 text-xs text-gray-500">{footer}</div>
    </div>
  );

  if (highlight) {
    return (
      <Wrapper
        onClick={onClick}
        className={`p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] block ${
          interactive ? "cursor-pointer hover:shadow-lg transition-shadow" : ""
        }`}
      >
        {inner}
      </Wrapper>
    );
  }

  return (
    <Wrapper
      onClick={onClick}
      className={`rounded-2xl border border-gray-200 bg-white block ${
        interactive ? "cursor-pointer hover:border-genie-primary/40 hover:shadow-sm transition-all" : ""
      }`}
    >
      <div className="p-5 md:p-6 h-full flex flex-col">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-xl bg-genie-primary/10 flex items-center justify-center">
            <GenieIcon name={genieIcon} size={24} className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-[#64748B]">{label}</p>
        </div>
        <p className="text-3xl font-bold text-[#222B45] leading-none mb-2">{value}</p>
        <div className="mt-auto pt-3 text-xs text-gray-500">{footer}</div>
      </div>
    </Wrapper>
  );
};

const DeltaPill = ({ delta }: { delta: number | null }) => {
  const { label, tone } = formatDelta(delta);

  if (tone === "new" || tone === "flat") {
    return <span className="text-xs text-gray-500">{label}</span>;
  }

  const Icon = tone === "up" ? TrendingUp : TrendingDown;
  const colorClass = tone === "up" ? "text-genie-primary" : "text-red-600";

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${colorClass}`}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
};

const SkeletonTile = () => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 h-[148px] animate-pulse">
    <div className="flex items-center gap-3 mb-3">
      <div className="h-10 w-10 rounded-xl bg-gray-100" />
      <div className="h-4 w-24 bg-gray-100 rounded" />
    </div>
    <div className="h-8 w-16 bg-gray-100 rounded mb-3" />
    <div className="h-3 w-32 bg-gray-100 rounded" />
  </div>
);

const WeeklyDigest = ({ summary, isLoading, isError, onAttentionClick, onSilentClick }: WeeklyDigestProps) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SkeletonTile />
        <SkeletonTile />
        <SkeletonTile />
        <SkeletonTile />
      </div>
    );
  }

  // No data — typically because the insights endpoint isn't deployed yet, or
  // the user has no genies. Render placeholder tiles so the layout doesn't
  // collapse and the page still feels intentional.
  if (!summary || isError) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Tile
          genieIcon="chat"
          label="Conversations"
          value="—"
          footer={<span className="text-xs text-gray-400">No data yet</span>}
        />
        <Tile
          genieIcon="chat-check"
          label="Resolved without you"
          value="—"
          footer={<span className="text-xs text-gray-400">No data yet</span>}
        />
        <Tile
          genieIcon="eye"
          label="Needs attention"
          value="—"
          footer={<span className="text-xs text-gray-400">No data yet</span>}
        />
        <Tile
          genieIcon="genie-lamp"
          label="Silent genies"
          value="—"
          footer={<span className="text-xs text-gray-400">No data yet</span>}
        />
      </div>
    );
  }

  const attentionPositive = summary.attention.current > 0;
  const silentPositive = summary.silentGenies.current > 0;
  // Conversations that never triggered a handoff/escalation — the "your Genies
  // finished the job" count that replaced the retired leads tile.
  const resolved = Math.max(0, summary.conversations.current - (summary.attention.escalations ?? 0));

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <Tile
        genieIcon="chat"
        label="Conversations"
        value={summary.conversations.current.toLocaleString()}
        footer={<DeltaPill delta={summary.conversations.delta} />}
      />
      <Tile
        genieIcon="chat-check"
        label="Resolved without you"
        value={resolved.toLocaleString()}
        footer={
          <span>
            of {summary.conversations.current.toLocaleString()} conversation
            {summary.conversations.current === 1 ? "" : "s"}
          </span>
        }
      />
      <Tile
        genieIcon="eye"
        label="Needs attention"
        value={summary.attention.current.toLocaleString()}
        highlight={attentionPositive}
        onClick={attentionPositive ? onAttentionClick : undefined}
        footer={
          attentionPositive ? (
            <span className="text-genie-secondary font-medium">Review now →</span>
          ) : (
            <span>All clear this week</span>
          )
        }
      />
      <Tile
        genieIcon="genie-lamp"
        label="Silent genies"
        value={`${summary.silentGenies.current}`}
        highlight={silentPositive}
        onClick={silentPositive ? onSilentClick : undefined}
        footer={
          <span>
            of {summary.silentGenies.total} total
            {silentPositive ? <span className="text-genie-secondary font-medium"> · review →</span> : null}
          </span>
        }
      />
    </div>
  );
};

export default WeeklyDigest;
